import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Divider } from '#/components/Divider/Divider';
import { useFormContext } from '#/components/Form/FormContext';
import { FormSection } from '#/components/Form/FormSection';
import { useFormSubmittedInterceptor } from '#/components/Form/FormSubmittedInterceptor';
import { GeneralError } from '#/components/Form/GeneralError';
import { SecondarySubmitButton } from '#/components/Form/SubmitButton/SecondarySubmitButton';
import { Icon } from '#/components/Icon/Icon';
import { BodySmall } from '#/components/Text/BodySmall';
import { H4 } from '#/components/Text/H4';
import { TabContext } from '#/containers/TabContext';
import { useDeepEffect } from '#/hooks/useDeepEffect';
import { usePrevious } from '#/hooks/usePrevious';
import { useI18n } from '#/i18n/hooks/useI18n';
import { MultiStepFormLayout } from '#/layouts/MultiStepForm/MultiStepFormLayout';
import { colors, gutterWidth } from '#/theme/theme';
import { trackPageView } from '#/utils/analytics/track/trackPageView';
import { isDefined } from '#/utils/functional/type';

import type {
  MultiStepFormStep,
  MultiStepFormStepRow,
} from './MultiStepFormTypes';
import { FormStep } from './components/FormStep';
import { MultiStepFormHeader } from './components/MultiStepFormHeader';
import { PreviousErrorsSection } from './components/PreviousErrorsSection';
import { SectionFields } from './components/SectionFields';
import { SidebarImage } from './components/SidebarImage';
import { SmallHeader } from './components/SmallHeader';
import { StyledForm } from './components/StyledForm';
import { SubmitButton } from './components/SubmitButton';
import { ToggleSection } from './components/ToggleSection';
import { getFieldsToValidate } from './helpers/getFieldsToValidate';
import { getFirstStepWithError } from './helpers/getFirstStepWithError';
import { getPreviousErrorsByStep } from './helpers/getPreviousErrorsByStep';
import { useMultiStep } from './useMultiStep';

type SidebarSection = {
  imageUrl?: string;
  body?: ReactNode;
  width?: number;
};

type Props = {
  disableConfirmTabClose?: boolean;
  steps: MultiStepFormStep[];
  fields: Record<string, ReactNode>;
  isDraft?: boolean;
  isSaveDraftEnabled?: boolean;
  intro?: ReactNode;
  trackingTitle?: string;
  title?: string;
  singlePage?: boolean;
  noButtons?: boolean;
  sidebar?: SidebarSection[];
  logo?: ReactNode;
  successSection?: ReactNode;
  submitText?: string;
  hasTracking?: boolean;
  hideHeader?: boolean;
  large?: boolean;
  cartDropdownButton?: ReactNode;
};

export function MultiStepForm({
  disableConfirmTabClose = false,
  steps: stepsProp,
  fields,
  isDraft,
  isSaveDraftEnabled = false,
  intro,
  trackingTitle,
  title,
  singlePage,
  noButtons,
  sidebar,
  logo,
  successSection,
  submitText,
  hasTracking = false,
  hideHeader,
  large = !singlePage,
  cartDropdownButton,
}: Props) {
  const { i18n } = useI18n();

  const {
    edited,
    errors,
    submitForm,
    getValue,
    setErrors,
    submitting,
    updateValues,
  } = useFormContext();

  const { currentStep, navigateToStep } = useMultiStep();

  const steps: MultiStepFormStep[] = singlePage
    ? stepsProp.reduce(
        (
          acc,
          {
            title: stepTitle,
            preTitle,
            disabled,
            beforeSection,
            glowUp,
            sections,
          },
        ) => {
          const [s] = acc;

          if (stepTitle && !(sections.length && sections[0].title)) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-param-reassign
            sections[0].title = stepTitle;
            // eslint-disable-next-line no-param-reassign
            sections[0].preTitle = preTitle;
            // eslint-disable-next-line no-param-reassign
            sections[0].disabled = disabled;
            // eslint-disable-next-line no-param-reassign
            sections[0].beforeSection = beforeSection;
            // eslint-disable-next-line no-param-reassign
            sections[0].glowUp = glowUp;
          }

          return [
            {
              // @ts-expect-error TS(2769): No overload matches this call.
              sections: s.sections.concat(sections),
            },
          ];
        },
        [
          {
            sections: [],
          },
        ],
      )
    : stepsProp;
  const stepRef = useRef(null);
  const formRef = useRef(null);
  const isTabbing = useContext(TabContext) || false;
  const prevState = usePrevious({
    isTabbing,
    currentStep,
  });
  const hasSidebar = Boolean(sidebar);
  const Section = hasSidebar ? 'div' : FormSection;

  const stepEventName =
    steps[currentStep]?.eventName ?? `Multi-Step Form: Step ${currentStep}`;
  const stepEventProperties = steps[currentStep]?.eventProperties ?? {};

  const previousErrorsByStep: string[][] = errors
    ? getPreviousErrorsByStep({
        steps,
        currentStep,
        errors,
      })
    : [];
  const previousErrors = previousErrorsByStep.flat();

  useEffect(() => {
    if (submitting || singlePage || !edited || disableConfirmTabClose) return;

    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [disableConfirmTabClose, edited, singlePage, submitting]);

  useDeepEffect(() => {
    if (hasTracking) {
      trackPageView(stepEventName, {
        step_index: currentStep,
        ...stepEventProperties,
      });
    }
  }, [currentStep, hasTracking, stepEventName, stepEventProperties]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (stepRef && stepRef.current) {
      // @ts-expect-error TS(2315): Type 'NodeList' is not generic.
      const inputsList: NodeList<HTMLElement> =
        // @ts-expect-error TS(2339): Property 'querySelectorAll' does not exist on type... Remove this comment to see the full error message
        stepRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]',
        );
      // set tabindex=0 for all input elements except those with data-skip-tab or data-no-tab set to "true"
      const inputs = Array.prototype.slice.call(inputsList).filter((input) => {
        const noTabAttr = input.getAttribute('data-no-tab');
        const skipTabAttr = input.getAttribute('data-skip-tab');
        return (
          (!noTabAttr || noTabAttr !== 'true') &&
          (!skipTabAttr || skipTabAttr !== 'true')
        );
      });

      inputs.forEach((el: HTMLElement) => {
        // eslint-disable-next-line no-param-reassign
        el.tabIndex = 0;
      });

      if (isTabbing && prevState && currentStep !== prevState.currentStep) {
        inputs[0].focus();
      }
    }
  }, [currentStep, isTabbing, prevState]);

  const mappedHeaderSteps = steps.map((step) => ({
    title: step.title,
    ignoreInNav: step.ignoreInNav,
  }));

  const seenFields = {};

  const submittedInterceptor = () => {
    if (!isDraft || getValue('hasNextStep')) return;

    const firstStepWithError = getFirstStepWithError(steps, errors);
    if (firstStepWithError !== null) {
      setErrors(
        {
          general: i18n(
            'Please complete all required fields in order to save this draft.',
          ),
        },
        true,
      );
      if (firstStepWithError === currentStep) return;

      navigateToStep(firstStepWithError);
    }
  };

  useFormSubmittedInterceptor({
    onIntercept: submittedInterceptor,
  });

  const submitDraft = useCallback(() => {
    return submitForm({
      publish: false,
      hasNextStep: false,
      validate: getFieldsToValidate(steps, currentStep),
    });
  }, [currentStep, steps, submitForm]);

  const submitStep = useCallback(
    (stepIndex: number) => {
      return updateValues({
        publish: false,
        hasNextStep: true,
        validate: getFieldsToValidate(steps, stepIndex),
      });
    },
    [steps, updateValues],
  );

  const submitFormWithPublish = useCallback(
    (stepIndex: number) => {
      return updateValues({
        publish: true,
        hasNextStep: false,
        validate: getFieldsToValidate(steps, stepIndex),
      });
    },
    [steps, updateValues],
  );

  return (
    <MultiStepFormLayout
      sidebar={
        sidebar && (
          <>
            {sidebar.map((section, index) =>
              index === currentStep ? (
                <Box // oxlint-disable-next-line react/no-array-index-key
                  key={index}
                  width={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  px={[gutterWidth / 2, null, null, gutterWidth]}
                  pt={gutterWidth / 2}
                  pb={(gutterWidth * 3) / 2}
                  textAlign="center"
                >
                  <SidebarImage width={section.width} maxWidth="100%" mx="auto">
                    <img src={section.imageUrl} alt="" />
                  </SidebarImage>

                  {section.body && (
                    <Box
                      mt={36}
                      maxWidth={400}
                      color={colors.lightContentGrey}
                      component={BodySmall}
                    >
                      {section.body}
                    </Box>
                  )}
                </Box>
              ) : null,
            )}
          </>
        )
      }
    >
      <div ref={formRef}>
        <StyledForm>
          {!hideHeader && (
            <Box
              mb={hasSidebar ? 36 : 0}
              bg="white"
              position="relative"
              zIndex={2}
            >
              {hasSidebar ? (
                <SmallHeader steps={mappedHeaderSteps} logo={logo} />
              ) : (
                <MultiStepFormHeader
                  steps={mappedHeaderSteps}
                  submitDraft={submitDraft}
                  errors={previousErrorsByStep}
                  trackingTitle={trackingTitle}
                  title={title}
                  edited={edited}
                  singlePage={singlePage}
                  isDraft={isDraft}
                  cartDropdownButton={cartDropdownButton}
                />
              )}
            </Box>
          )}
          <Box position="relative" flex="1 1 auto">
            {successSection ??
              steps.map((step, stepIndex) =>
                stepIndex === currentStep ? (
                  <FormStep
                    $active={currentStep === stepIndex}
                    key={step.title ?? stepIndex}
                    bg={hasSidebar ? 'white' : undefined}
                  >
                    {step.loadingSection ?? (
                      <div ref={currentStep === stepIndex ? stepRef : null}>
                        {isDefined(intro) && step.showIntro ? intro : null}
                        {previousErrors.length > 0 && (
                          <PreviousErrorsSection large>
                            <H4>{i18n('Previous Errors')}</H4>

                            <BodySmall mb={40}>
                              {i18n(
                                'We found these errors on previous pages. You may fix them here, or go back to make further changes.',
                              )}
                            </BodySmall>

                            <>{previousErrors.map((error) => fields[error])}</>
                          </PreviousErrorsSection>
                        )}

                        <GeneralError
                          ignore={step.sections.map(
                            ({ matchError }) => matchError,
                          )}
                        />

                        {step.sections.map((section, sectionIndex) => {
                          const lastStep = stepIndex === steps.length - 1;

                          return (
                            <Section // oxlint-disable-next-line react/no-array-index-key
                              key={sectionIndex}
                              preTitle={section.preTitle}
                              disabled={section.disabled}
                              beforeSection={section.beforeSection}
                              glowUp={section.glowUp}
                              title={hasSidebar ? undefined : section.title}
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...(hasSidebar
                                ? undefined
                                : {
                                    large,
                                    description: section.intro,
                                    compactDescription:
                                      section?.introOptions?.compact,
                                    bodyPaddingBottom: noButtons
                                      ? ['4px', null, 34]
                                      : undefined,
                                  })}
                            >
                              {section.actions && (
                                <Box position="relative">
                                  <Box
                                    position="absolute"
                                    right={[-36, null, -60]}
                                    top={[-24, null, -28]}
                                  >
                                    {section.actions.map((action, index) => (
                                      // TODO: Fix this the next time the file is edited.
                                      // oxlint-disable-next-line react/no-array-index-key
                                      <Box ml={12} key={index}>
                                        {action}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {hasSidebar && (
                                <>
                                  <H4>{section.title}</H4>

                                  {section.intro && (
                                    <Box mb={24}>{section.intro}</Box>
                                  )}
                                </>
                              )}

                              {section.matchError && (
                                <GeneralError match={section.matchError} />
                              )}

                              {section.rows.map(
                                (
                                  row: MultiStepFormStepRow | null | undefined,
                                  rowIndex,
                                ) =>
                                  row ? (
                                    <ToggleSection
                                      title={row.toggle} // oxlint-disable-next-line react/no-array-index-key
                                      key={rowIndex}
                                      toggleQaId={row.toggleQaId}
                                    >
                                      <SectionFields
                                        row={row}
                                        fields={fields}
                                        seenFields={seenFields}
                                      />
                                    </ToggleSection>
                                  ) : (
                                    <Divider
                                      // oxlint-disable-next-line react/no-array-index-key
                                      key={rowIndex}
                                      marginTop={54}
                                      marginBottom={54}
                                    />
                                  ),
                              )}

                              {sectionIndex === step.sections.length - 1 &&
                                stepIndex === currentStep &&
                                !noButtons && (
                                  <Box
                                    display={['flex', null, null, 'flex']}
                                    width="calc(100% + 16px)"
                                    mx="-8px"
                                    mt={
                                      hasSidebar
                                        ? gutterWidth
                                        : [gutterWidth, null, null, gutterWidth]
                                    }
                                    justifyContent="flex-end"
                                  >
                                    {stepIndex > 0 && (
                                      <Box p="8px" mr="auto">
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          icon={{
                                            type: 'normalized',
                                            name: 'arrow-left',
                                          }}
                                          size="large"
                                          onClick={() => {
                                            navigateToStep(stepIndex - 1);
                                          }}
                                          qaId="multi-form-prev-step"
                                          title={i18n('Back')}
                                        />
                                      </Box>
                                    )}
                                    {lastStep && isSaveDraftEnabled ? (
                                      <Box p="8px">
                                        <SecondarySubmitButton
                                          qaId="save-draft"
                                          type="button"
                                          size="large"
                                          onClick={submitDraft}
                                        >
                                          {i18n('Save Draft')}
                                        </SecondarySubmitButton>
                                      </Box>
                                    ) : null}
                                    {step.leftButtons && (
                                      <Box p="8px">{step.leftButtons}</Box>
                                    )}

                                    {/* TODO: Fix this the next time the file is edited. */}
                                    {/* oxlint-disable-next-line no-nested-ternary */}
                                    {lastStep ? (
                                      <Box p="8px">
                                        <SubmitButton
                                          size="large"
                                          onClick={() =>
                                            submitFormWithPublish(stepIndex)
                                          }
                                        >
                                          {step.nextText ??
                                            submitText ??
                                            i18n('Finish & Publish')}
                                          <Box display="flex" ml="4px">
                                            <Icon
                                              name="arrow-right"
                                              size={18}
                                            />
                                          </Box>
                                        </SubmitButton>
                                      </Box>
                                    ) : !step.hideNextStep ? (
                                      <Box p="8px">
                                        <SubmitButton
                                          disabled={step.submitIsDisabled}
                                          size="large"
                                          onClick={() => submitStep(stepIndex)}
                                          qaId="multi-form-next-step"
                                        >
                                          {step.nextText ?? i18n('Next')}
                                          <Box ml="4px" display="inline-block">
                                            <Icon
                                              name="arrow-right"
                                              size={18}
                                            />
                                          </Box>
                                        </SubmitButton>
                                      </Box>
                                    ) : null}
                                  </Box>
                                )}
                            </Section>
                          );
                        })}
                      </div>
                    )}
                  </FormStep>
                ) : null,
              )}
          </Box>
          {steps[currentStep]?.footer ? steps[currentStep].footer : null}
        </StyledForm>
      </div>
    </MultiStepFormLayout>
  );
}
